import {
  OPEN_TICKET_STATUSES,
  SUPPORT_ATTACHMENT_MAX_BYTES,
  SUPPORT_ATTACHMENT_TYPES,
  type SupportMessage,
  type SupportReasonGroup,
  type TicketStatus,
  type TicketView,
} from '@capsico/contracts';

/* Pure helpers for the Help screens (no React, no I/O): message merging, photo checks, grouping. */

/** Optimistic (not yet saved) messages carry this id prefix. */
export const LOCAL_PREFIX = 'local-';

/** Adds or replaces one message (by id) and keeps the thread in time order. Internal notes never show. */
export function mergeSupportMessage(
  view: TicketView | undefined,
  message: SupportMessage,
): TicketView | undefined {
  if (!view || message.ticketId !== view.id || message.internal) return view;
  const rest = view.messages.filter((entry) => entry.id !== message.id);
  const messages = [...rest, message].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  return { ...view, messages };
}

/** Swaps an optimistic bubble for the saved one (or drops it when `saved` is null). */
export function replaceLocalMessage(
  view: TicketView | undefined,
  tempId: string,
  saved: TicketView | null,
): TicketView | undefined {
  const without = view
    ? { ...view, messages: view.messages.filter((entry) => entry.id !== tempId) }
    : view;
  return saved ? keepNewer(without, saved) : without;
}

/**
 * The server copy wins when it is at least as new as ours; optimistic bubbles that the server has
 * not echoed yet stay at the end so the thread never flickers.
 */
export function keepNewer(previous: TicketView | undefined, fresh: TicketView): TicketView {
  if (!previous || previous.id !== fresh.id) return fresh;
  if (previous.version > fresh.version) return previous;
  const pending = previous.messages.filter((entry) => entry.id.startsWith(LOCAL_PREFIX));
  if (pending.length === 0) return fresh;
  return { ...fresh, messages: [...fresh.messages, ...pending] };
}

export function isOpenStatus(status: TicketStatus): boolean {
  return OPEN_TICKET_STATUSES.includes(status);
}

export type PhotoProblem = 'type' | 'size' | null;

/** Only JPEG / PNG / WebP up to the size limit may be sent. */
export function photoProblem(file: { type: string; size: number }): PhotoProblem {
  if (!(SUPPORT_ATTACHMENT_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size <= 0 || file.size > SUPPORT_ATTACHMENT_MAX_BYTES) return 'size';
  return null;
}

/** Reasons grouped in the order they arrive (the server sorts them). */
export function groupReasons<T extends { group: SupportReasonGroup }>(
  reasons: readonly T[],
): { group: SupportReasonGroup; reasons: T[] }[] {
  const out: { group: SupportReasonGroup; reasons: T[] }[] = [];
  for (const reason of reasons) {
    const bucket = out.find((entry) => entry.group === reason.group);
    if (bucket) bucket.reasons.push(reason);
    else out.push({ group: reason.group, reasons: [reason] });
  }
  return out;
}

/** A stable op key for one submit attempt (8–128 of A–Z a–z 0–9 _ -). */
export function newOpKey(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 128);
}
